import { kstParts } from '@biznomi/ui-react'
import { api, apiForm, fetchBlob } from './client'

/**
 * 급여명세서 전달 — 우리는 **급여를 계산하지 않는다.**
 *
 *   금액        파싱하지 않는다. 파일 안을 읽지 않는다
 *   합계·정산   내지 않는다. 회사가 급여 시스템에서 뽑은 걸 그대로 나른다
 *   공제 검증   하지 않는다
 *
 * 우리가 하는 일 — **바뀌지 않았음을 증명(sha256)** 하고, **본인만 열게 하고**,
 * **열람 사실을 남기는 것**(= 교부 증빙). 계약서와 달리 서명은 없다.
 */

export type PayslipStatus = 'SENT' | 'VIEWED' | 'CANCELLED'

export interface MyPayslip {
  id: string
  /** "2026-08" */
  payPeriod: string
  fileName: string
  fileSize: number
  fileHash: string
  status: PayslipStatus
  viewedAt: string | null
  createdAt: string
}

export interface AdminPayslip extends MyPayslip {
  employeeId: string
  memo: string | null
  employee: {
    name: string
    employeeNumber: string | null
    department: { name: string } | null
  }
  uploadedBy: { name: string } | null
}

/** 파일명이 누구에게 갈지 — 보내기 전 확인용 */
export interface PayslipMatch {
  fileName: string
  employeeId: string | null
  employeeName: string | null
  employeeNumber: string | null
  /** 매칭 실패 이유. 성공이면 null */
  reason: string | null
}

export interface PayslipPreview {
  payPeriod: string
  matches: PayslipMatch[]
  matchedCount: number
  failedCount: number
  /** 이 급여월에 이미 보낸 건 */
  existing: { id: string; employeeId: string; fileName: string; createdAt: string }[]
}

// ── 직원 ─────────────────────────────────────────────────────

export function getMyPayslips() {
  return api<MyPayslip[]>('/api/payslips/my')
}

export function getMyUnviewedPayslipCount() {
  return api<{ count: number }>('/api/payslips/my/unviewed-count')
}

// ── 관리자 (ADMIN 전용 — 부서장도 못 본다) ───────────────────

export function getPayslips(
  params: {
    page?: number
    limit?: number
    payPeriod?: string
    status?: PayslipStatus
    search?: string
  } = {},
) {
  const q = new URLSearchParams()
  if (params.page) q.set('page', String(params.page))
  if (params.limit) q.set('limit', String(params.limit))
  if (params.payPeriod) q.set('payPeriod', params.payPeriod)
  if (params.status) q.set('status', params.status)
  if (params.search) q.set('search', params.search)
  return api<{ items: AdminPayslip[]; total: number; page: number; limit: number }>(
    `/api/payslips?${q.toString()}`,
  )
}

export function getPayslipPeriods() {
  return api<string[]>('/api/payslips/periods')
}

/**
 * 보내기 전 미리보기 — **파일은 아직 안 보낸다.** 이름만 보내서
 * 누구에게 갈지 확인한다. 오발송이 이 기능에서 가장 큰 사고다.
 */
export function previewPayslips(payPeriod: string, fileNames: string[]) {
  return api<PayslipPreview>('/api/payslips/preview', {
    method: 'POST',
    body: JSON.stringify({ payPeriod, fileNames }),
  })
}

export interface PayslipUploadResult {
  payPeriod: string
  createdCount: number
  replacedCount: number
  failed: { fileName: string; reason: string }[]
}

export function uploadPayslips(input: {
  payPeriod: string
  files: File[]
  replaceExisting: boolean
}) {
  const form = new FormData()
  form.append('payPeriod', input.payPeriod)
  form.append('replaceExisting', String(input.replaceExisting))
  // 파일이 여러 개여도 같은 필드명으로 append 하면 서버 multer .array('files') 가 받는다
  for (const f of input.files) form.append('files', f)
  return apiForm<PayslipUploadResult>('/api/payslips', form)
}

export function cancelPayslip(id: string) {
  return api<AdminPayslip>(`/api/payslips/${id}/cancel`, { method: 'PATCH' })
}

// ── 파일 열람 ────────────────────────────────────────────────

export async function openPayslipFile(id: string): Promise<{ url: string } | { error: string }> {
  const res = await fetchBlob(`/api/payslips/${id}/file`)
  if ('error' in res) return res
  return { url: URL.createObjectURL(res.blob) }
}

// ── 유틸 ─────────────────────────────────────────────────────

/** "2026-08" → "2026년 8월" */
export function payPeriodLabel(p: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(p)
  if (!m) return p
  return `${m[1]}년 ${Number(m[2])}월`
}

/**
 * 이번 달과 지난 몇 달을 급여월 후보로 준다 (최신 먼저).
 *
 * 기준은 **KST 이번 달**이다. `new Date().getMonth()` 를 쓰면 기기 타임존을 따르는데,
 * 월말·월초에 KST 와 다른 지역이면 한 달이 어긋난다 (급여월은 한 번 틀리면
 * 그 달 명세서가 통째로 다른 달에 들어간다).
 */
export function recentPayPeriods(count = 15): string[] {
  const { year, month } = kstParts(Date.now())
  return Array.from({ length: count }, (_, i) => {
    // 월을 0-based 로 내린 뒤 다시 1-based 로. 연도 넘김은 Date 가 알아서 한다
    const d = new Date(Date.UTC(year, month - 1 - i, 1))
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
  })
}

export function fileSizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`
}
